import React, { useState, useEffect } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { useAuth0 } from "@auth0/auth0-react";
import "./styles/CustomCheckout.css";
import CheckoutForm from "./CheckoutForm";
import { loadStripe } from "@stripe/stripe-js";

// Initialize Stripe with your publishable key
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLIC_KEY as string
);

// Interface for SingleStepCheckout props
interface SingleStepCheckoutProps {
  customerId?: string;
  promoCode?: string;
  hideHeaderText?: boolean;
  onPromoCodeClear?: () => void;
}

// Main component that wraps the checkout form with Stripe Elements
const SingleStepCheckout: React.FC<SingleStepCheckoutProps> = ({
  customerId = "",
  promoCode = "",
  hideHeaderText = true,
  onPromoCodeClear,
}) => {
  const { user, isAuthenticated, isLoading } = useAuth0();
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [error] = useState<string | null>(null); // Error state for displaying errors

  useEffect(() => {
    // Wait for Auth0 to load
    if (isLoading) return;

    // If not authenticated, user will be redirected by ProtectedRoute
    if (!isAuthenticated || !user) return;

    // Once we have user data from Auth0, we're ready to show the checkout
    setIsDataLoading(false);

    // Log authentication status
    console.log("User authenticated for checkout:", user.email);
  }, [isLoading, isAuthenticated, user]);

  // Use provided customerId or get from Auth0 user
  const finalCustomerId = customerId || user?.sub || "";

  // Get final promo code - either from props (for modal) or URL params (for standalone page)
  const finalPromoCode =
    promoCode ||
    (() => {
      const params = new URLSearchParams(window.location.search);
      return params.get("promo_code") || "";
    })();

  if (isLoading || isDataLoading) {
    return (
      <div className="custom-checkout-container">
        <div className="loading-container">
          <p>Loading checkout information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="custom-checkout-container">
        <div className="error-container">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="custom-checkout-container">
      <Elements stripe={stripePromise}>
        <CheckoutForm
          customerId={finalCustomerId}
          promoCode={finalPromoCode}
          userEmail={user?.email || ""}
          hideHeaderText={hideHeaderText}
          onPromoCodeClear={onPromoCodeClear}
        />
      </Elements>
    </div>
  );
};

export default SingleStepCheckout;
